import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, Link, QrCode, Search, CircleX } from "lucide-react";
import { deleteAppointment, fetchAppointments, getLocation, loadAppointmentLink, updateAppointment } from "../../services/appointment";
import { debounce, exchangeDate, formatDate, generateBookAppointmentTableData, getDateString, handelCopyLink, handleQRScanPage, numberWithCommas } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { notify, getPractice } from "../../helpers";
import { getSMSLimitData } from "../../services/message.js";
import DataTablePagination from "../DataTablePagination";
import Spacing from "../Spacing";
import AppointmentsDataTable from "../DataTable/AppointmentsDataTable";
import TabMenu from "../TabMenu";
import DatePicker from "react-datepicker";
import AppointmentSingleViewModal from "../Modal/AppointmentSingleViewModal.jsx";


export default function Appointments({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }
  let dateStr = getDateString();

  const [dataLoading, setDataLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [activePatientViewModal, setActivePatientViewModal] = useState(false);
  const [patientData, setPatientData] = useState({});
  const [dateRange, setDateRange] = useState([dateStr, dateStr]);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    status: "",
    appointment_type: "",
    search_term: "",
    start_date: dateStr,
    end_date: dateStr,
    location: "",
    account_id: account_id,
    location_id: location_id,
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    allCount: 0,
    totalPages: 0,
    completedCount: 0,
    expiredCount: 0,
    pendingCount: 0,
    confirmedCount: 0,
    cancelledCount: 0,
    rescheduledCount: 0,
    account_id: account_id,
    location_id: location_id,
  });
  const [allLocation, setAllLocation] = useState([]);
  const [appointmentPortalLink, setAppointmentPortalLink] = useState('');
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  // Handle single date change
  const handleDateChange = (update) => {
    const [start, end] = update;

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
        page: 1,
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
        page: 1,
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
        page: 1,
      });
    }

    setDateRange([start, end]);
  };

  useEffect(() => {
    loadAppointments(filter);
  }, [filter]);

  useEffect(() => {
    getAppointmentLink(account_id);
  }, [account_id]);

  const getAppointmentLink = async (accountId) => {
    try {
      const response = await loadAppointmentLink(accountId);
      if(response) {
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setAppointmentPortalLink(response.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getAppointmentLink ~ error:", error);
    }
  };

  // Loads the locations asynchronously.
  const loadLocations = async (id, accountId) => {
    try {
      const locationResponse = await getLocation(id, accountId);
      setAllLocation(locationResponse.data || []);
    } catch (error) {
      // console.log("🚀 ~ loadLocations ~ error:", error);
    }
  };

  // Loads appointments based on the provided filter.
  const loadAppointments = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const appointmentResponse = await fetchAppointments(filter);
      const generateData = generateBookAppointmentTableData(
        appointmentResponse.data.results
      );
      setAppointments(generateData);
      setPaginationCount({
        allCount: appointmentResponse.data.all_count,
        totalCount: appointmentResponse.data.count,
        totalPages: appointmentResponse.data.total_pages,
        completedCount: appointmentResponse.data.completed_count,
        expiredCount: appointmentResponse.data.expired_count,
        pendingCount: appointmentResponse.data.pending_count,
        confirmedCount: appointmentResponse.data.confirmed_count,
        cancelledCount: appointmentResponse.data.cancelled_count,
        rescheduledCount: appointmentResponse.data.reschedule_count,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadAppointments ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  // Handles the checkbox change event for a specific appointment.
  const handleCheckboxChange = (id) => {
    setAppointments((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
  };

  // Handles the select all functionality for appointments.
  const handleSelectAll = () => {
    const newData = appointments.map((row) => ({
      ...row,
      checked: !selectAll,
    }));
    setAppointments(newData);
    setSelectAll(!selectAll);
  };

  // Retrieves appointments based on the provided filter.
  const getAppointmentsByStatus = (selectedTabValue) => {
    const tab = selectedTabValue.split(" ")[0];
    const tabValue = tab.toLowerCase() === "all" ? "" : tab.toLowerCase();
    setFilter({ ...filter, status: tabValue, page: 1 });
    // loadAppointments({ ...filter, status: tabValue, page: 1 });
  };

  // Handles the page change event.
  const handlePageChange = (newPage) => {
    setDataLoading(true);
    setFilter({ ...filter, page: newPage });
  };

  // Handles the change event for the search term input field.
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  // Handles the change of rows per page in the filter.
  const handleChangeRowsPerPage = (value) => {
    setDataLoading(true);
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
  };

  // Array containing the tab menu data.
  const tabMenuData = [
    `All (${paginationCount.allCount})`,
    `Completed (${paginationCount.completedCount})`,
    `Confirmed (${paginationCount.confirmedCount})`,
    `Pending (${paginationCount.pendingCount})`,
    `Cancelled (${paginationCount.cancelledCount})`,
    `Rescheduled (${paginationCount.rescheduledCount})`,
    `Expired (${paginationCount.expiredCount})`,
  ];

  const getSMSLimit = async() => {
    const result = await getSMSLimitData(account_id);
    if (result) {
      setSmsLimit(result?.data);
    }
  }

  // Handles the confirmation of an appointment.
  const handleAppointmentConfirmation = async (appointmentData) => {
    setDataLoading(true);
    try {
      const result = await updateAppointment(appointmentData);
      if(result){
        notify("success", "Appointment confirmed successfully", 0.5);
        setTimeout(() => {
          getSMSLimit();
        }, 3000);
        await loadAppointments(filter);
      }
    } catch (error) {
      // console.log("🚀 ~ handleAppointmentConfirmation ~ error:", error);
      // console.error(error);
    }
  };

  // Handles the completion of an appointment.
  const handleAppointmentCompleted = async (id, appointmentTime, hexId) => {
    setDataLoading(true);
    const appointmentData = {
      account_id: account_id,
      location_id: location_id,
      status: "completed",
      appointment_time: appointmentTime,
      hex_id: hexId,
    };
    try {
      const result = await updateAppointment(appointmentData);
      if(result){
        notify("success", "Appointment completed successfully", 0.5);
        setTimeout(() => {
          getSMSLimit();
        }, 3000);
        await loadAppointments(filter);
      }
    } catch (error) {
      // console.log("🚀 ~ handleAppointmentCompleted ~ error:", error);
      // console.error(error);
    }
  };

  // Handles the cancellation of an appointment.
  const handleAppointmentCancellation = async (appointmentData) => {
    setDataLoading(true);
    try {
      const result = await updateAppointment(appointmentData);
      if(result){
        notify("success", "Appointment cancelled successfully", 0.5);
        setTimeout(() => {
          getSMSLimit();
        }, 3000);
        await loadAppointments(filter);
      }
    } catch (error) {
      // console.log("🚀 ~ handleAppointmentCancellation ~ error:", error);
      // console.error(error);
    }
  };

  // Deletes an appointment record.
  const handleDeleteRecord = async (id) => {
    setDataLoading(true);
    try {
      const result = await deleteAppointment(
        id,
        account_id,
        location_id
      );
      if(result){
        notify("success", "Record deleted successfully.", 0.5);
        await loadAppointments(filter);
      }
    } catch (error) {
      // console.log("🚀 ~ handleDeleteRecord ~ error:", error);
    }
  };

  const handleSelectLocation = (e) => {
    setDataLoading(true);
    setFilter({ ...filter, location_id: e.target.value, page: 1 });
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      page: 1,
    });
    loadLocations(account_id);
    getAppointmentLink(account_id);
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleAppointmentViewModal = (data) => {
    setPatientData(data);
    setActivePatientViewModal(!activePatientViewModal);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  }

  useDocumentTitle("Appointments");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_violet_bg docpro_white_color">
            <CalendarDays width={20} height={20} />
          </i>
          Appointments
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="4" sm="4" />
      <div>
        <TabMenu
          tabMenuData={tabMenuData}
          filter={false}
          handleSelectTab={getAppointmentsByStatus}
          setDataLoading={setDataLoading}
        />
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings docpro_grid_gap_10">
          <div
            className={`flex items-center docpro_grid_gap_10 ${
              filter.start_date !== filter.end_date
                ? "docpro_have_date_filter2"
                : ""
            }`}
          >
            <form className="docpro_search_form">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            <div className={`flex items-center docpro_grid_gap_4`}>
              <div
                className={`docpro_toggle_calendar ${
                  filter.start_date ? "active" : ""
                }`}
              >
                <CalendarDays />
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}
              </div>
              {filter.start_date && filter.end_date && (
                <span
                  onClick={() => {
                    setFilter({
                      ...filter,
                      start_date: "",
                      end_date: "",
                      page: 1,
                    });
                    setDateRange([null, null]);
                    setDataLoading(true);
                  }}
                >
                  <CircleX
                    size={22}
                    color="var(--accent)"
                    className="pointer"
                  />
                </span>
              )}
            </div>
          </div>
          <div className="docpro_data_table_btns p-0">
            {getPractice("website") !== null && getPractice("website") ? (
              <>
                <button
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={() => handleQRScanPage("appointment")}
                >
                  <QrCode width={20} height={20} />
                  Form QR code
                </button>
                <button
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={() => handelCopyLink(`${appointmentPortalLink}`)}
                >
                  <Link width={20} height={20} />
                  Form link
                </button>
              </>
            ) : ( null )}
            <select
              className="docpro_form_field"
              style={{ width: "150px", padding: "5px 27px 5px 12px" }}
              onChange={handleSelectLocation}
              value={filter.location_id}
            >
              <option value="">All Locations</option>
              {allLocation.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <AppointmentsDataTable
          tableData={appointments}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          handleAppointmentConfirmation={handleAppointmentConfirmation}
          handleAppointmentCancellation={handleAppointmentCancellation}
          handleAppointmentCompleted={handleAppointmentCompleted}
          handleDeleteRecord={handleDeleteRecord}
          reloadData={loadAppointments}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          handleAppointmentViewModal={handleAppointmentViewModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          smsLimit={smsLimit}
        />
        {appointments.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {activePatientViewModal && (
        <AppointmentSingleViewModal
          practice_data={patientData}
          activeModal={activePatientViewModal}
          setActiveModal={setActivePatientViewModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
